import type { SupabaseClient } from '@supabase/supabase-js'

export const TEST_MODE_COOKIE = 'cms_test_mode'

export type DataSchema = 'public' | 'test'

export function resolveSchema(cookieValue: string | undefined): DataSchema {
  return cookieValue === '1' ? 'test' : 'public'
}

export function readTestModeCookieFromString(cookieHeader: string | undefined): string | undefined {
  if (!cookieHeader) return undefined
  const match = cookieHeader.match(new RegExp(`(?:^|;\\s*)${TEST_MODE_COOKIE}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

export function getBrowserSchema(): DataSchema {
  if (typeof document === 'undefined') return 'public'
  return resolveSchema(readTestModeCookieFromString(document.cookie))
}

// `profiles` and `staff` are identity-adjacent tables that are deliberately never
// cloned into the `test` schema (see docs/superpowers/specs/2026-06-28-test-mode-data-routing-design.md).
// Any client whose default schema follows the test-mode cookie must still reach
// these two tables in `public`, or queries against them 404 with PGRST205
// ("Could not find the table 'test.profiles'") whenever test mode is active —
// this broke post-login profile hydration in practice.
const NEVER_CLONED_TABLES = new Set(['profiles', 'staff'])

/**
 * Route table access to the active data schema on every `.from()` call.
 *
 * The browser Supabase client is a singleton; pinning schema only at construction
 * leaves it on `public` if anything touched the client before the test-mode
 * cookie was readable. Re-resolve on each query so reads and writes stay aligned
 * with `cms_test_mode`.
 */
export function pinIdentityTablesToPublicSchema<T extends SupabaseClient<any, any, any, any, any>>(
  client: T,
  resolveDomainSchema: () => DataSchema = getBrowserSchema
): T {
  client.from = ((table: string) => {
    if (NEVER_CLONED_TABLES.has(table)) {
      return client.schema('public').from(table)
    }
    return client.schema(resolveDomainSchema()).from(table)
  }) as T['from']
  return client
}
